import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';

interface TicketSLAIndicatorProps {
  sla_due_at: string | null;
  status: string;
  priority: string;
  created_at: string;
}

export function TicketSLAIndicator({ sla_due_at, status, priority, created_at }: TicketSLAIndicatorProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [slaStatus, setSlaStatus] = useState<'ok' | 'warning' | 'overdue'>('ok');

  useEffect(() => {
    if (!sla_due_at || status === 'resolved' || status === 'closed') {
      return;
    }

    const updateTimeRemaining = () => {
      const now = new Date();
      const dueDate = new Date(sla_due_at);
      const timeDiff = dueDate.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeRemaining('Vencido');
        setSlaStatus('overdue');
        return;
      }

      // Calculate percentage of SLA time elapsed
      const createdDate = new Date(created_at);
      const totalSlaTime = dueDate.getTime() - createdDate.getTime();
      const elapsedTime = now.getTime() - createdDate.getTime();
      const percentageElapsed = (elapsedTime / totalSlaTime) * 100;

      // Set status based on percentage
      if (percentageElapsed >= 90) {
        setSlaStatus('overdue');
      } else if (percentageElapsed >= 75) {
        setSlaStatus('warning');
      } else {
        setSlaStatus('ok');
      }

      // Format time remaining
      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 24) {
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;
        setTimeRemaining(`${days}d ${remainingHours}h`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [sla_due_at, status, created_at]);

  if (status === 'resolved' || status === 'closed') {
    return (
      <Badge variant="secondary" className="gap-1">
        <CheckCircle className="h-3 w-3" />
        Concluído
      </Badge>
    );
  }

  if (status === 'paused') {
    return (
      <Badge variant="outline" className="gap-1">
        <Clock className="h-3 w-3" />
        Pausado
      </Badge>
    );
  }

  if (!sla_due_at) {
    return (
      <Badge variant="outline" className="gap-1">
        <Clock className="h-3 w-3" />
        Sem SLA
      </Badge>
    );
  }

  const getVariantByStatus = () => {
    switch (slaStatus) {
      case 'ok':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'overdue':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getIconByStatus = () => {
    switch (slaStatus) {
      case 'ok':
        return <CheckCircle className="h-3 w-3" />;
      case 'warning':
        return <Clock className="h-3 w-3" />;
      case 'overdue':
        return <AlertTriangle className="h-3 w-3" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  return (
    <Badge variant={getVariantByStatus()} className="gap-1">
      {getIconByStatus()}
      {timeRemaining}
    </Badge>
  );
}